import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { XCircle, Loader2, Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

interface BookingItem {
  id: number;
  status: string;
  parent_name: string;
  start_time: string;
  end_time: string;
  teacher_first_name: string;
  teacher_last_name: string;
}

export default function AppointmentCancelAll() {
  const { groupToken } = useParams<{ groupToken: string }>();
  const navigate = useNavigate();
  const [emailSent, setEmailSent] = useState(false);

  const { data: bookings, isLoading, error } = useQuery<BookingItem[]>(
    ['appointments', 'cancel-all', groupToken],
    async () => {
      const response = await api.get(`/appointments/public/cancel-all/${groupToken}`);
      return response.data.data;
    },
    { enabled: !!groupToken, retry: 1 }
  );

  const cancelAllMutation = useMutation(
    async () => {
      const response = await api.post(`/appointments/public/cancel-all/${groupToken}`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setEmailSent(true);
        toast.success(data?.message || 'E-Mail zur Bestätigung der Stornierung wurde versendet.');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Stornierung');
      }
    }
  );

  if (isLoading || !groupToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !bookings || bookings.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Keine Buchungen gefunden</h1>
          <p className="text-gray-600 mb-4">
            Die angeforderte Buchungsgruppe existiert nicht oder alle Termine wurden bereits storniert.
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold mb-4">E-Mail versendet</h1>
          <p className="text-gray-600 mb-6">
            An die hinterlegte E-Mail-Adresse wurde ein Link zur Bestätigung der Stornierung aller Termine gesendet.
            Bitte bestätigen Sie innerhalb von 30 Minuten.
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold mb-4">Alle Termine stornieren</h1>
        <p className="text-gray-600 mb-4">
          Die folgenden {bookings.length} Termin(e) werden storniert, sobald Sie die Stornierung per E-Mail-Link bestätigen:
        </p>
        <ul className="space-y-3 mb-6">
          {bookings.map((b) => (
            <li key={b.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded">
              <Calendar className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <div>
                <div className="font-semibold">
                  {b.teacher_first_name} {b.teacher_last_name}
                </div>
                <div className="text-sm text-gray-600">
                  {format(parseISO(b.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} –{' '}
                  {format(parseISO(b.end_time), 'HH:mm', { locale: de })}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-6 text-sm text-amber-800">
          Sie erhalten eine E-Mail mit einem Bestätigungslink. Die Stornierung wird erst nach Klick auf den Link wirksam (gültig 30 Min.).
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/appointments/all')}
            className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            onClick={() => cancelAllMutation.mutate()}
            disabled={cancelAllMutation.isLoading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {cancelAllMutation.isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Wird gesendet...
              </>
            ) : (
              'Stornierung per E-Mail anfordern'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
